import { getRowDateTime, formatDisplayDateTime } from './dateTimeFormat';

export function normalizeLoadOn(loadOn) {
  const s = String(loadOn ?? '').trim().toLowerCase();
  if (s === 'kseb') return 'KSEB';
  if (s === 'inverter') return 'Inverter';
  return '';
}

function dateKeyFromMs(ms) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function ensureDailyBucket(map, key) {
  if (!map.has(key)) {
    map.set(key, { date: key, ksebMs: 0, inverterMs: 0, unknownMs: 0 });
  }
  return map.get(key);
}

function addDurationToDaily(map, source, startMs, endMs) {
  let cursor = startMs;
  while (cursor < endMs) {
    const dayKey = dateKeyFromMs(cursor);
    const dayEnd = new Date(
      new Date(cursor).getFullYear(),
      new Date(cursor).getMonth(),
      new Date(cursor).getDate() + 1,
      0,
      0,
      0,
      0
    ).getTime();
    const sliceEnd = Math.min(endMs, dayEnd);
    const sliceMs = sliceEnd - cursor;
    const bucket = ensureDailyBucket(map, dayKey);
    if (source === 'KSEB') bucket.ksebMs += sliceMs;
    else if (source === 'Inverter') bucket.inverterMs += sliceMs;
    else bucket.unknownMs += sliceMs;
    cursor = sliceEnd;
  }
}

function pushSession(sessions, source, startMs, endMs) {
  if (!source || endMs <= startMs) return;
  const last = sessions[sessions.length - 1];
  if (last && last.source === source && last.endMs === startMs) {
    last.endMs = endMs;
    return;
  }
  sessions.push({ source, startMs, endMs });
}

/**
 * Derive KSEB / Inverter working hours from report history rows.
 * Each reading's loadOn applies until the next reading (or window end).
 */
export function computeWorkingHours(rows, windowFrom, windowTo) {
  if (!windowFrom || !windowTo || windowTo <= windowFrom) {
    return {
      ksebMs: 0,
      inverterMs: 0,
      unknownMs: 0,
      totalWindowMs: 0,
      sampleCount: 0,
      sessions: [],
      dailyBreakdown: []
    };
  }

  const windowStartMs = windowFrom.getTime();
  const windowEndMs = windowTo.getTime();
  const totalWindowMs = windowEndMs - windowStartMs;

  const sorted = (Array.isArray(rows) ? rows : [])
    .map((row) => ({ row, t: getRowDateTime(row) }))
    .filter(({ t }) => t && !isNaN(t.getTime()) && t >= windowFrom && t <= windowTo)
    .sort((a, b) => a.t.getTime() - b.t.getTime());

  let ksebMs = 0;
  let inverterMs = 0;
  let unknownMs = 0;
  const sessions = [];
  const dailyMap = new Map();

  if (sorted.length === 0) {
    return {
      ksebMs: 0,
      inverterMs: 0,
      unknownMs: totalWindowMs,
      totalWindowMs,
      sampleCount: 0,
      sessions: [],
      dailyBreakdown: []
    };
  }

  for (let i = 0; i < sorted.length; i++) {
    const { row, t } = sorted[i];
    const source = normalizeLoadOn(row.loadOn);
    const startMs = t.getTime();
    const endMs =
      i < sorted.length - 1 ? sorted[i + 1].t.getTime() : windowEndMs;
    const duration = endMs - startMs;
    if (duration <= 0) continue;

    addDurationToDaily(dailyMap, source, startMs, endMs);
    pushSession(sessions, source, startMs, endMs);

    if (source === 'KSEB') ksebMs += duration;
    else if (source === 'Inverter') inverterMs += duration;
    else unknownMs += duration;
  }

  const firstMs = sorted[0].t.getTime();
  if (firstMs > windowStartMs) {
    const gap = firstMs - windowStartMs;
    unknownMs += gap;
    addDurationToDaily(dailyMap, '', windowStartMs, firstMs);
  }

  const dailyBreakdown = Array.from(dailyMap.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => {
      const dayTotal = d.ksebMs + d.inverterMs + d.unknownMs;
      return {
        ...d,
        dayTotalMs: dayTotal,
        ksebHours: msToHours(d.ksebMs),
        inverterHours: msToHours(d.inverterMs),
        unknownHours: msToHours(d.unknownMs),
        ksebPercent: dayTotal > 0 ? (d.ksebMs / dayTotal) * 100 : 0,
        inverterPercent: dayTotal > 0 ? (d.inverterMs / dayTotal) * 100 : 0
      };
    });

  return {
    ksebMs,
    inverterMs,
    unknownMs,
    totalWindowMs,
    sampleCount: sorted.length,
    sessions: sessions.map((s) => ({
      ...s,
      durationMs: s.endMs - s.startMs,
      startLabel: formatDisplayDateTime(new Date(s.startMs).toISOString()),
      endLabel: formatDisplayDateTime(new Date(s.endMs).toISOString()),
      durationLabel: formatDuration(s.endMs - s.startMs)
    })),
    dailyBreakdown,
    firstReading: sorted[0].t,
    lastReading: sorted[sorted.length - 1].t
  };
}

export function msToHours(ms) {
  return ms / (1000 * 60 * 60);
}

export function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '0s';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || h > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
}

export function formatHoursDecimal(ms, digits = 2) {
  return msToHours(ms).toFixed(digits);
}

export function formatPercent(value, digits = 1) {
  if (!Number.isFinite(value)) return '0.0%';
  return `${value.toFixed(digits)}%`;
}
